import type {
	NotificationCategory,
	NotificationCenterItem,
	NotificationImageLayout,
} from './types';

type BrazeCardLike = {
	id?: string;
	viewed?: boolean;
	pinned?: boolean;
	isControl?: boolean;
	dismissed?: boolean;
	dismissible?: boolean;
	extras?: Record<string, string>;
	updated?: Date | null;
	title?: string;
	description?: string;
	imageUrl?: string;
	url?: string;
	linkText?: string;
	/** Native SDKs expose CTA label as `domain`; web SDK uses `linkText`. */
	domain?: string;
	constructor?: { name?: string };
	/** Braze web-sdk internal card type marker (e.g. `ab-classic-card`). */
	ae?: string;
};

type BrazeCardKind = 'classic' | 'captioned' | 'imageOnly' | 'unknown';

export function mapCategory(extras?: Record<string, string>): NotificationCategory {
	const raw = extras?.category?.toLowerCase();
	return raw === 'highlights' ? 'highlights' : 'account';
}

/**
 * Resolve Braze card kind via constructor name / internal markers only.
 * ponytail: avoid static `@braze/web-sdk` imports here — Drawer/Settings pull this
 * module for the unread badge; `instanceof` against a static import also fails when
 * the SDK was loaded via dynamic `import()`.
 */
export function getBrazeCardKind(card: BrazeCardLike): BrazeCardKind {
	const name = card.constructor?.name;
	if (name === 'CaptionedImage') {
		return 'captioned';
	}
	if (name === 'ImageOnly') {
		return 'imageOnly';
	}
	if (name === 'ClassicCard') {
		return 'classic';
	}

	if (card.ae === 'ab-captioned-image') {
		return 'captioned';
	}
	if (card.ae === 'ab-image-only') {
		return 'imageOnly';
	}
	if (card.ae === 'ab-classic-card') {
		return 'classic';
	}

	return 'unknown';
}

export function mapImageLayout(card: BrazeCardLike): NotificationImageLayout | undefined {
	if (!card.imageUrl?.trim()) {
		return undefined;
	}

	const kind = getBrazeCardKind(card);
	if (kind === 'classic') {
		return 'thumbnail';
	}
	if (kind === 'captioned' || kind === 'imageOnly') {
		return 'hero';
	}
	// Unknown card shape with an image — treat as hero (matches mobile full-width).
	return 'hero';
}

export function mapBrazeCardToItem(card: BrazeCardLike): NotificationCenterItem | null {
	if (!card?.id || card.isControl || card.dismissed) {
		return null;
	}

	const rawTitle = card.title?.trim() ?? '';
	const rawDescription = card.description?.trim() ?? '';
	const imageUrl = card.imageUrl?.trim() || undefined;

	if (!rawTitle && !rawDescription && !imageUrl) {
		return null;
	}

	const title = rawTitle || rawDescription;
	const body = rawTitle && rawDescription && rawTitle !== rawDescription ? rawDescription : '';

	const ctaText = (card.linkText || card.domain)?.trim() || undefined;
	const ctaUrl = card.url?.trim() || undefined;
	const isPinned = Boolean(card.pinned);

	return {
		id: card.id,
		title,
		body,
		imageUrl,
		imageLayout: mapImageLayout(card),
		ctaText: ctaText && ctaUrl ? ctaText : undefined,
		ctaUrl: ctaText && ctaUrl ? ctaUrl : undefined,
		category: mapCategory(card.extras),
		isPinned,
		// iOS parity: Spotlight/pinned cards are never dismissible on web.
		isDismissible: Boolean(card.dismissible) && !isPinned,
		isRead: Boolean(card.viewed),
		// Web SDK exposes `updated` rather than createdAt; use it for sort/buckets.
		createdAt: card.updated?.getTime() ?? 0,
	};
}

export function sortNotificationItems(
	items: NotificationCenterItem[]
): NotificationCenterItem[] {
	return [...items].sort((a, b) => {
		if (a.isPinned !== b.isPinned) {
			return a.isPinned ? -1 : 1;
		}
		return b.createdAt - a.createdAt;
	});
}
